<template>
  <v-combobox
    v-model="toolboxItems"
    :items="globalfilterToolboxStore.toolboxItems"
    item-value="label"
    item-title="label"
    clearable
    :label="labels.GLOBAL_FILTER.DEFINE_NEW_CONSTRAINT"
    multiple
    variant="underlined"
    open-on-clear
    chips
    :loading="loading"
    no-filter
    :menu-props="{ maxWidth: '100%' }"
    autocomplete
    hint="Enter artifact type, name, or attribute. Press Enter to apply."
    :disabled="props.disabled"
    :rules="[validConstraint]"
    auto-select-first
    @update:model-value="onUpdateModelValue"
    @update:search="onUpdateSearch"
  >
    <template #item="{ item, index, props: itemProps }">
      <v-list-item v-bind="itemProps" :key="index" :title="item.raw.label">
        <template #prepend>
          <v-icon
            v-if="getItemIcon(item.raw) != ''"
            class="mr-1"
            :icon="getItemIcon(item.raw)"
          ></v-icon>
        </template>
      </v-list-item>
    </template>

    <template v-if="hasNextPage" #append-item>
      <v-list-item>
        <div class="d-flex justify-center">
          <v-btn variant="outlined" @click.prevent="onLoadMoreItems">Load more</v-btn>
        </div>
      </v-list-item>
    </template>

    <template #chip="{ item, props: itemProps }">
      <v-chip
        :variant="getItemIcon(item.raw) != '' ? 'tonal' : 'outlined'"
        size="small"
        label
        v-bind="itemProps"
      >
        {{ item.raw.label }}
        <template #prepend>
          <v-icon
            v-if="getItemIcon(item.raw) != ''"
            class="mr-1"
            :icon="getItemIcon(item.raw)"
          ></v-icon>
        </template>
      </v-chip>
    </template>
  </v-combobox>

  <v-checkbox
    v-if="isAttributeConstraint"
    :label="labels.GLOBAL_FILTER.CHECKBOX_IGNORE_MISSING"
    :disabled="props.disabled"
    :true-value="true"
    :false-value="true"
    @update:model-value="setIgnoreIfMissing"
  >
  </v-checkbox>
</template>

<script lang="ts" setup>
  import { Expression, SingleExpression, ToolboxItem } from '@/models'
  import { debounce } from 'lodash'
  import { computed, onMounted, ref, watch } from 'vue'
  import labels from '@/translations/labels.json'
  import { useRoute } from 'vue-router'
  import { VCombobox } from 'vuetify/lib/components/index.mjs'
  import { useGlobalFilterToolboxStore } from '@/stores/globalFilterToolboxStore'
  import { hasToolboxExpression } from '@/utils/global-filters'

  const emit = defineEmits<{
    addNewConstraint: [value: Expression]
  }>()

  const route = useRoute()
  const projectID = computed(() => parseInt(route.params.projectId as string))

  const page = ref<number>(1)
  const query = ref<string>()
  const loading = ref<boolean>(false)
  const toolboxItems = ref<ToolboxItem[]>([])

  const globalfilterToolboxStore = useGlobalFilterToolboxStore()

  const props = defineProps<{ disabled: boolean }>()

  const expressionModel = defineModel<SingleExpression>('expression')

  onMounted(async () => {
    await globalfilterToolboxStore.getToolboxItems(projectID.value)
  })

  watch(toolboxItems, async (items, old) => {
    if (items.length > 0) {
      expressionModel.value = items[items.length - 1].value as SingleExpression
    }

    const result: ToolboxItem[] = []

    if (old.length > items.length) {
      for (let i = 0; i < Math.min(items.length, old.length); i++) {
        if (items[i] === old[i]) {
          result.push(items[i])
        } else {
          break // Stop at the first mismatch
        }
      }

      if (result.length !== toolboxItems.value.length) {
        toolboxItems.value = result

        // Avoid race condition with the request triggered by the combobox model update listener
        await new Promise((resolve) => setTimeout(resolve, 1000))

        globalfilterToolboxStore.toolboxItems = []
        await fetchNewToolboxItems()
      }
    }
  })

  watch(
    () => globalfilterToolboxStore.toolboxItems,
    (items) => {
      if (items.length > 0) {
        const last = items[items.length - 1]
        if (last.type === 'new_constraint') {
          globalfilterToolboxStore.toolboxItems = []
          emit('addNewConstraint', last.value)
        }
      }
    },
  )

  const onUpdateModelValue = () => {
    // TODO: in the future we will probably need custom string literals (for artifacts that are not yet in the snapshot,
    // or other comparisons: contains, gt, lt, etc...)
    toolboxItems.value = toolboxItems.value.filter((i) => typeof i !== 'string')

    globalfilterToolboxStore.toolboxItems = []

    fetchNewToolboxItems()
  }

  const onUpdateSearch = debounce((search: string) => {
    globalfilterToolboxStore.toolboxItems = []

    page.value = 1
    query.value = search

    fetchNewToolboxItems(query.value, page.value)
  }, 400)

  const onLoadMoreItems = () => {
    page.value++

    fetchNewToolboxItems(query.value, page.value)
  }

  const fetchNewToolboxItems = async (query?: string, page?: number, pageSize?: number) => {
    loading.value = true

    if (toolboxItems.value.length > 0) {
      await globalfilterToolboxStore.getToolboxItemsForExpression(
        projectID.value,
        toolboxItems.value[toolboxItems.value.length - 1].value,
        { query, page, pageSize },
      )
    } else {
      await globalfilterToolboxStore.getToolboxItems(projectID.value, { query, page, pageSize })
    }

    loading.value = false
  }

  const hasNextPage = computed(() => {
    const pagination = globalfilterToolboxStore.toolboxPagination

    if (!pagination) return false

    return pagination.page * pagination.pageSize < pagination.totalElements
  })

  const isAttributeConstraint = computed(() => {
    return toolboxItems.value[0]?.type === 'attribute_suggestion'
  })

  const setIgnoreIfMissing = (ignoreIfMissing: unknown) => {
    if (typeof ignoreIfMissing !== 'boolean') return

    toolboxItems.value.forEach((i) => {
      if (
        i.value.type === 'eq' ||
        i.value.type === 'ne' ||
        i.value.type === 'toolbox.assist.binary'
      ) {
        if (i.value.left.type === 'artifact.attribute') {
          i.value.left.ignore_if_missing = ignoreIfMissing
        }
      }
    })
  }

  const getItemIcon = (item: ToolboxItem) => {
    switch (item.type) {
      case 'add_equals':
        return 'mdi-equal'
      case 'add_not_equals':
        return 'mdi-not-equal'
      case 'property_suggestion':
        switch (item.label) {
          case 'Artifact Name':
            return 'mdi-rename-box'
          case 'Artifact Resource':
            return 'mdi-package'
          case 'Artifact Type Name':
            return 'mdi-alpha-t-box'
          case 'Artifact Type Category':
            return 'mdi-shape'
          default:
            return ''
        }
      case 'attribute_suggestion':
        return 'mdi-card-bulleted'
      default:
        return ''
    }
  }

  function validConstraint(): boolean | string {
    const expression = toolboxItems.value.at(toolboxItems.value.length - 1)?.value

    if (!expression) return false

    return !hasToolboxExpression(expression)
  }
</script>
